import { MAX_RULE_ACTION_AMOUNT, MAX_RULE_GROUP_DEPTH } from "../constants";
import { isDateRangeOrdered } from "./date";
import { ConditionOperator, ConditionValueType } from "../enums";
import {
  CONDITION_FIELD_METADATA,
  RULE_ACTION_METADATA,
} from "../rule-metadata";
import type { ConditionField } from "../enums";
import type {
  IEarningRuleInput,
  IRuleAction,
  IRuleCondition,
  IRuleConditionGroup,
} from "../models/rule";

/* --------------------------- Serialized DTOs --------------------------- */

/** Wire shape of a condition (UI-only fields like `valueLabels` removed). */
export interface IConditionDTO {
  field: string;
  operator: string;
  value?: string | number | boolean | null;
  values?: Array<string | number>;
  range?: { min: number | string; max: number | string };
}

/** Wire shape of a recursive condition group. */
export interface IConditionGroupDTO {
  operator: string;
  conditions: IConditionDTO[];
  groups: IConditionGroupDTO[];
}

/**
 * Serializes a builder condition group to the backend wire shape, dropping
 * client-only presentation fields (ids, value labels). Deterministic and pure.
 */
export function serializeConditionGroup(group: IRuleConditionGroup): IConditionGroupDTO {
  return {
    operator: group.operator,
    conditions: group.conditions.map(serializeCondition),
    groups: group.groups.map(serializeConditionGroup),
  };
}

function serializeCondition(condition: IRuleCondition): IConditionDTO {
  const dto: IConditionDTO = {
    field: condition.field,
    operator: condition.operator,
  };
  if (condition.value !== undefined) dto.value = condition.value;
  if (condition.values !== undefined) dto.values = condition.values;
  if (condition.range !== undefined) dto.range = condition.range;
  return dto;
}

/**
 * Rebuilds a builder condition group from a wire DTO, assigning fresh ids via
 * the injected factory (keeps the function pure / SSR-safe).
 */
export function deserializeConditionGroup(
  dto: IConditionGroupDTO,
  idFactory: () => string,
): IRuleConditionGroup {
  return {
    id: idFactory(),
    operator: dto.operator as IRuleConditionGroup["operator"],
    conditions: dto.conditions.map((c) => deserializeCondition(c, idFactory)),
    groups: dto.groups.map((g) => deserializeConditionGroup(g, idFactory)),
  };
}

function deserializeCondition(
  dto: IConditionDTO,
  idFactory: () => string,
): IRuleCondition {
  return {
    id: idFactory(),
    field: dto.field as ConditionField,
    operator: dto.operator as ConditionOperator,
    value: dto.value,
    values: dto.values,
    range: dto.range,
  };
}

/* ----------------------------- Factories ------------------------------ */

/**
 * Creates a fresh condition for a field, pre-selecting the first valid operator
 * and an appropriate empty value container based on the field metadata.
 */
export function createCondition(
  field: ConditionField,
  idFactory: () => string,
): IRuleCondition {
  const meta = CONDITION_FIELD_METADATA[field];
  const operator = meta.operators[0] ?? ConditionOperator.EQUALS;
  const condition: IRuleCondition = { id: idFactory(), field, operator };

  switch (meta.valueType) {
    case ConditionValueType.MULTI_ENTITY:
    case ConditionValueType.MULTI_ENUM:
      condition.values = [];
      break;
    case ConditionValueType.DATE_RANGE:
      condition.range = { min: "", max: "" };
      break;
    case ConditionValueType.BOOLEAN:
      condition.value = true;
      break;
    default:
      condition.value = null;
  }
  return condition;
}

/* ----------------------------- Traversal ------------------------------ */

/** Total number of leaf conditions across the whole tree. */
export function countConditions(group: IRuleConditionGroup): number {
  return (
    group.conditions.length +
    group.groups.reduce((sum, g) => sum + countConditions(g), 0)
  );
}

/** All leaf conditions flattened depth-first. */
export function flattenConditions(group: IRuleConditionGroup): IRuleCondition[] {
  return [
    ...group.conditions,
    ...group.groups.flatMap((g) => flattenConditions(g)),
  ];
}

/** Maximum nesting depth of the tree (root group counts as depth 1). */
export function groupDepth(group: IRuleConditionGroup): number {
  return 1 + group.groups.reduce((deepest, child) => Math.max(deepest, groupDepth(child)), 0);
}

/* ----------------------------- Validation ----------------------------- */

/**
 * Validates an earning-rule input, returning translation-key error codes.
 * Empty array = valid. Covers the cases required by the spec: empty condition
 * groups, missing actions, invalid multipliers, invalid limits, bad dates and
 * excessive nesting.
 */
export function validateEarningRule(input: IEarningRuleInput): string[] {
  const errors: string[] = [];

  if (!input.name.trim()) errors.push("rule_name_required");

  if (input.actions.length === 0) {
    errors.push("rule_actions_required");
  }
  for (const action of input.actions) {
    errors.push(...validateAction(action));
  }

  // Date window sanity.
  if (!isDateRangeOrdered(input.startDate, input.endDate)) {
    errors.push("rule_date_range_invalid");
  }

  // Nesting depth.
  if (groupDepth(input.conditions) > MAX_RULE_GROUP_DEPTH) {
    errors.push("rule_nesting_too_deep");
  }

  // No empty (childless) non-root groups.
  if (hasEmptyGroup(input.conditions)) {
    errors.push("rule_empty_condition_group");
  }

  // Each leaf condition must carry a value appropriate to its operator.
  for (const condition of flattenConditions(input.conditions)) {
    if (!isConditionComplete(condition)) {
      errors.push("rule_condition_incomplete");
      break;
    }
  }

  if (input.priority < 0) errors.push("rule_priority_invalid");

  for (const limit of input.limits) {
    if (limit.type !== "ONE_TIME_ONLY" && limit.value <= 0) {
      errors.push("rule_limit_invalid");
      break;
    }
  }

  return errors;
}

/**
 * Whether an action amount is accepted by the backend: a finite number with
 * `0 < amount ≤ MAX_RULE_ACTION_AMOUNT`, for every action type.
 */
export function isRuleActionAmountValid(amount: number): boolean {
  return Number.isFinite(amount) && amount > 0 && amount <= MAX_RULE_ACTION_AMOUNT;
}

/**
 * A non-positive multiplier reports `rule_multiplier_invalid`; every other
 * amount outside {@link isRuleActionAmountValid} reports `rule_action_amount_invalid`.
 */
function validateAction(action: IRuleAction): string[] {
  if (isRuleActionAmountValid(action.amount)) return [];

  const isNonPositive = !(Number.isFinite(action.amount) && action.amount > 0);
  return [RULE_ACTION_METADATA[action.type].isMultiplier && isNonPositive ? "rule_multiplier_invalid" : "rule_action_amount_invalid"];
}

/** A non-root group with no conditions and no subgroups is invalid. */
function hasEmptyGroup(group: IRuleConditionGroup, isRoot = true): boolean {
  if (!isRoot && group.conditions.length === 0 && group.groups.length === 0) {
    return true;
  }
  return group.groups.some((g) => hasEmptyGroup(g, false));
}

/** Whether a leaf condition has a value consistent with its operator. */
export function isConditionComplete(condition: IRuleCondition): boolean {
  switch (condition.operator) {
    case ConditionOperator.IS_TRUE:
    case ConditionOperator.IS_FALSE:
      return true;
    case ConditionOperator.IN:
    case ConditionOperator.NOT_IN:
      return Array.isArray(condition.values) && condition.values.length > 0;
    case ConditionOperator.BETWEEN:
      return (
        condition.range != null &&
        condition.range.min !== "" &&
        condition.range.max !== ""
      );
    default:
      return (
        condition.value !== null &&
        condition.value !== undefined &&
        condition.value !== ""
      );
  }
}
